import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, BedDouble, Calculator, Check, HardHat, House, MapPin, Scale, Stamp } from "lucide-react";
import { post } from "../lib/api";
import { leadSourceFields } from "../lib/leadSource";
import { measureLead } from "../lib/openaiPixel";
import { Eyebrow, ArrowCta, Reveal, MaskLineView } from "../components/ui-bits";
import { useLang } from "../i18n/LangContext";

const HERO_IMG = "https://images.pexels.com/photos/35907807/pexels-photo-35907807.jpeg?auto=compress&cs=tinysrgb&w=1600";

const field =
  "w-full rounded-lg border border-sand bg-white px-4 py-3 text-sm outline-none transition-colors placeholder:text-fog/60 focus:border-verde-700";
const label = "mb-1.5 block text-xs font-medium text-ink";

const COPY = {
  en: {
    heroEyebrow: "Expert Network",
    heroL1: "Local knowledge behind",
    heroL2: "every credit decision.",
    heroCopy:
      "Real estate is local. The Verde Capital Expert Network brings planning, legal, technical and market specialists into the underwriting process — where their knowledge matters most.",
    heroAlt: "Historic Lisbon facade",
    typesEyebrow: "Types of experts",
    roles: [
      { icon: MapPin, t: "Local market specialists" },
      { icon: Stamp, t: "Planning experts" },
      { icon: HardHat, t: "Technical consultants" },
      { icon: Calculator, t: "Valuation professionals" },
      { icon: Scale, t: "Legal experts" },
      { icon: House, t: "Residential specialists" },
      { icon: BedDouble, t: "Hospitality specialists" },
    ],
    joinCard: "Join the network",
    contributeEyebrow: "How experts contribute",
    contributeH: "AI-enabled. Expert-underwritten.",
    contribute: [
      { t: "Local knowledge", c: "On-the-ground insight into parishes, municipalities and market realities that data alone can't capture." },
      { t: "Sector expertise", c: "Specialists who know apartments, student housing, senior housing, co-living, serviced apartments and hotels from the inside." },
      { t: "Sharper underwriting", c: "Expert input helps Verde Capital's credit team make faster, better-informed decisions." },
    ],
    contributeLine: "AI makes underwriting faster. Humans make credit decisions.",
    joinEyebrow: "Contribute your expertise",
    joinH: "Join the Expert Network.",
    doneH: "Application received.",
    doneCopy: "Verde Capital will be in touch shortly.",
    name: "Name *",
    email: "Email *",
    specialty: "Specialty *",
    specialtyPh: "Select specialty",
    specialties: ["Planning", "Legal", "Technical", "Valuation", "Local market", "Residential", "Hospitality", "Other"],
    location: "Location",
    locationPh: "e.g. Lisbon",
    practice: "About your practice",
    failed: "Something went wrong — please try again.",
    submitting: "Submitting…",
    submit: "Join the Expert Network",
    questions: "Questions? Contact Verde Capital",
  },
  pt: {
    heroEyebrow: "Rede de Especialistas",
    heroL1: "Conhecimento local por trás",
    heroL2: "de cada decisão de crédito.",
    heroCopy:
      "O imobiliário é local. A Rede de Especialistas da Verde Capital traz planeamento, especialistas legais, técnicos e de mercado para o processo de subscrição — onde o seu conhecimento mais importa.",
    heroAlt: "Fachada histórica de Lisboa",
    typesEyebrow: "Tipos de especialistas",
    roles: [
      { icon: MapPin, t: "Especialistas em mercado local" },
      { icon: Stamp, t: "Especialistas em planeamento" },
      { icon: HardHat, t: "Consultores técnicos" },
      { icon: Calculator, t: "Profissionais de avaliação" },
      { icon: Scale, t: "Especialistas legais" },
      { icon: House, t: "Especialistas residenciais" },
      { icon: BedDouble, t: "Especialistas em hotelaria" },
    ],
    joinCard: "Juntar-se à rede",
    contributeEyebrow: "Como contribuem os especialistas",
    contributeH: "Com apoio de IA. Subscrito por especialistas.",
    contribute: [
      { t: "Conhecimento local", c: "Conhecimento de terreno sobre freguesias, municípios e realidades de mercado que os dados sozinhos não conseguem captar." },
      { t: "Especialização setorial", c: "Especialistas que conhecem de dentro apartamentos, habitação para estudantes, seniores, co-living, apartamentos de serviços e hotéis." },
      { t: "Subscrição mais rigorosa", c: "O contributo de especialistas ajuda a equipa de crédito da Verde Capital a decidir mais depressa e melhor informada." },
    ],
    contributeLine: "A IA torna a subscrição mais rápida. As pessoas tomam as decisões de crédito.",
    joinEyebrow: "Contribua com a sua experiência",
    joinH: "Junte-se à Rede de Especialistas.",
    doneH: "Candidatura recebida.",
    doneCopy: "A Verde Capital entrará em contacto em breve.",
    name: "Nome *",
    email: "Email *",
    specialty: "Especialidade *",
    specialtyPh: "Selecione a especialidade",
    specialties: ["Planeamento", "Legal", "Técnico", "Avaliação", "Mercado local", "Residencial", "Hotelaria", "Outro"],
    location: "Localização",
    locationPh: "ex.: Lisboa",
    practice: "Sobre a sua prática",
    failed: "Ocorreu um erro — tente novamente, por favor.",
    submitting: "A submeter…",
    submit: "Juntar-se à Rede de Especialistas",
    questions: "Perguntas? Fale com a Verde Capital",
  },
  nl: {
    heroEyebrow: "Expertennetwerk",
    heroL1: "Lokale kennis achter",
    heroL2: "elke kredietbeslissing.",
    heroCopy:
      "Vastgoed is lokaal. Het Expertennetwerk van Verde Capital brengt ruimtelijke-ordening-, juridische, technische en marktspecialisten samen in het beoordelingsproces — waar hun kennis het zwaarst weegt.",
    heroAlt: "Historische gevel in Lissabon",
    typesEyebrow: "Soorten experts",
    roles: [
      { icon: MapPin, t: "Marktexperts lokaal" },
      { icon: Stamp, t: "Ruimtelijke-ordeningsexperts" },
      { icon: HardHat, t: "Technische adviseurs" },
      { icon: Calculator, t: "Taxatieprofessionals" },
      { icon: Scale, t: "Juridische experts" },
      { icon: House, t: "Residentiële specialisten" },
      { icon: BedDouble, t: "Horeca-experts" },
    ],
    joinCard: "Toetreden tot het netwerk",
    contributeEyebrow: "Hoe experts bijdragen",
    contributeH: "AI-ondersteund. Beoordeeld door experts.",
    contribute: [
      { t: "Lokale kennis", c: "Kennis uit de praktijk over buurten, gemeenten en marktomstandigheden die data alleen niet kunnen vangen." },
      { t: "Sectorale specialisatie", c: "Specialisten die van binnenuit kennen: appartementen, studentenhuisvesting, seniorenwoningen, co-living, serviced apartments en hotels." },
      { t: "Scherpere beoordeling", c: "Expertinbreng helpt het kredietteam van Verde Capital sneller en beter geïnformeerd beslissen." },
    ],
    contributeLine: "AI maakt de beoordeling sneller. Mensen nemen de kredietbeslissing.",
    joinEyebrow: "Breng uw expertise in",
    joinH: "Word lid van het Expertennetwerk.",
    doneH: "Sollicitatie ontvangen.",
    doneCopy: "Verde Capital neemt zo snel mogelijk contact op.",
    name: "Naam *",
    email: "E-mail *",
    specialty: "Specialisme *",
    specialtyPh: "Kies een specialisme",
    specialties: ["Ruimtelijke ordening", "Juridisch", "Techniek", "Taxatie", "Lokale markt", "Residentiëel", "Horeca", "Anders"],
    location: "Locatie",
    locationPh: "bijv. Lissabon",
    practice: "Over uw praktijk",
    failed: "Er ging iets mis — probeer het opnieuw.",
    submitting: "Versturen…",
    submit: "Word lid van het Expertennetwerk",
    questions: "Vragen? Praat met Verde Capital",
  },
};

export default function ExpertNetwork() {
  const { lang, lp } = useLang();
  const c = COPY[lang];
  const [form, setForm] = useState({ name: "", email: "", specialty: "", location: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.name.trim() && /.+@.+\..+/.test(form.email) && form.specialty;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      await post("/expert-applications", { ...form, ...leadSourceFields() });
      measureLead();
      setDone(true);
    } catch {
      setFailed(true);
    }
    setSubmitting(false);
  };

  return (
    <div className="bg-cream">
      <section className="pt-32 sm:pt-40 pb-16 lg:pb-24">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <Eyebrow>{c.heroEyebrow}</Eyebrow>
            <h1 className="display mt-6 text-[2.6rem] sm:text-5xl lg:text-[56px] text-charcoal" data-testid="page-headline">
              <MaskLineView>{c.heroL1}</MaskLineView>
              <MaskLineView delay={0.12}>{c.heroL2}</MaskLineView>
            </h1>
            <Reveal delay={0.25} className="mt-7 max-w-lg">
              <p className="text-lg leading-relaxed text-ink">{c.heroCopy}</p>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="lg:col-span-5">
            <img src={HERO_IMG} alt={c.heroAlt} className="h-[340px] sm:h-[440px] w-full object-cover rounded-2xl" data-testid="network-hero-image" />
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white border-y border-sand">
        <div className="container-x">
          <Eyebrow>{c.typesEyebrow}</Eyebrow>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {c.roles.map((r, i) => (
              <Reveal key={r.t} delay={i * 0.05}>
                <div className="card flex h-full items-center gap-3.5 px-5 py-4" data-testid={`network-role-${i}`}>
                  <span className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-[#EAF3EE] via-[#E0EEE6] to-[#CFE5D9] ring-1 ring-verde-800/10 shadow-[0_10px_22px_-14px_rgba(7,39,29,0.4)]">
                    <span className="absolute inset-0 bg-gradient-to-b from-white/70 via-transparent to-transparent" aria-hidden="true" />
                    <r.icon className="relative h-4.5 w-4.5 text-verde-800" strokeWidth={1.7} aria-hidden="true" />
                  </span>
                  <p className="font-display text-sm font-bold text-charcoal">{r.t}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.35}>
              <a
                href="#join"
                data-testid="network-join-card"
                className="flex h-full items-center gap-3.5 rounded-2xl border border-dashed border-verde-700/50 bg-verde-50/60 px-5 py-4 transition-colors hover:border-verde-700 hover:bg-verde-50"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-verde-800 text-mint">
                  <ArrowRight className="h-4.5 w-4.5" strokeWidth={1.7} />
                </span>
                <p className="font-display text-sm font-bold text-verde-800">{c.joinCard}</p>
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-verde-950 text-cream grain relative">
        <div className="container-x relative">
          <Eyebrow light>{c.contributeEyebrow}</Eyebrow>
          <h2 className="display mt-5 text-3xl sm:text-4xl text-white max-w-2xl">
            <MaskLineView>{c.contributeH}</MaskLineView>
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {c.contribute.map((card, i) => (
              <Reveal key={card.t} delay={0.08 + i * 0.1}>
                <div className="h-full rounded-2xl border border-cream/15 bg-white/5 p-8" data-testid={`contribute-${i}`}>
                  <h3 className="font-display text-lg font-bold text-white">{card.t}</h3>
                  <p className="mt-3 text-sm text-cream/70 leading-relaxed">{card.c}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-12">
            <p className="font-display text-2xl font-bold text-white/90 max-w-xl">{c.contributeLine}</p>
          </Reveal>
        </div>
      </section>

      <section id="join" className="py-20 lg:py-28">
        <div className="container-x max-w-2xl">
          <Eyebrow>{c.joinEyebrow}</Eyebrow>
          <h2 className="display mt-5 text-3xl sm:text-4xl text-charcoal">
            <MaskLineView>{c.joinH}</MaskLineView>
          </h2>
          {done ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-10 rounded-2xl bg-white border border-sand p-10 text-center" data-testid="expert-confirmation">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-verde-800">
                <Check className="h-5 w-5 text-mint" />
              </span>
              <p className="mt-6 font-display text-2xl font-bold text-charcoal">{c.doneH}</p>
              <p className="mt-3 text-sm text-ink">{c.doneCopy}</p>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="mt-10 rounded-2xl bg-white border border-sand p-6 sm:p-10 space-y-6" data-testid="expert-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={label}>{c.name}</label>
                  <input className={field} value={form.name} onChange={set("name")} data-testid="expert-name" />
                </div>
                <div>
                  <label className={label}>{c.email}</label>
                  <input type="email" className={field} value={form.email} onChange={set("email")} data-testid="expert-email" />
                </div>
                <div>
                  <label className={label}>{c.specialty}</label>
                  <select className={field} value={form.specialty} onChange={set("specialty")} data-testid="expert-specialty">
                    <option value="" disabled>{c.specialtyPh}</option>
                    {c.specialties.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>{c.location}</label>
                  <input className={field} value={form.location} onChange={set("location")} placeholder={c.locationPh} data-testid="expert-location" />
                </div>
              </div>
              <div>
                <label className={label}>{c.practice}</label>
                <textarea rows={4} className={field} value={form.notes} onChange={set("notes")} data-testid="expert-notes" />
              </div>
              {failed && (
                <p className="text-sm text-red-600" data-testid="expert-error">
                  {c.failed}
                </p>
              )}
              <button
                type="submit"
                disabled={!valid || submitting}
                className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-verde-800 px-8 py-4 text-sm font-semibold text-white transition-all hover:bg-verde-700 disabled:cursor-not-allowed disabled:opacity-40"
                data-testid="expert-submit"
              >
                {submitting ? c.submitting : c.submit}
                {!submitting && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
              </button>
            </form>
          )}
          <Reveal delay={0.2} className="mt-10">
            <ArrowCta to={lp("/contact")} variant="ghost">{c.questions}</ArrowCta>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
